import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type {
  ContextRef,
  DurableFrame,
  ErrorCode,
  Frame,
  FrameOf,
  Frames,
  FrameType,
  OutgoingPart,
  PageState,
  PresenceTab,
  RunHead,
  RunStatus,
  ToolActivity,
  ToolOutcome,
  WireChoice,
  WireCommand,
  WireDraftFile,
  WireMessage,
} from "../protocol/protocol";
import { TIMED_OUT } from "../chat/pages";
import { DURABLE_FRAMES } from "../protocol/protocol";
import type { SandboxClientCall } from "../sandbox/core/hostCalls";
import { newerHost, type SandboxHost } from "../sandbox/host/role";
import { applyDelta, newLiveMessage, resetLiveStep, type LiveMessage } from "./live";
import { classifyRev } from "./rev";
import type { TargetKey } from "./targets";

export type ConnectionStatus =
  | "off"
  | "connecting"
  | "open"
  | "backoff"
  /** Closed from another tab (4410): the one state that shows the Reconnect banner. */
  | "tabClosed"
  /** The user may not use the assistant (ticket 403). */
  | "unavailable";

export const ACTIVE_RUN: readonly RunStatus[] = ["queued", "running"];
/** A run Stop ends: an active one, or one waiting on an event (a sandbox call the browser runs),
 * which the runtime does not count as running. */
export const STOPPABLE_RUN: readonly RunStatus[] = [...ACTIVE_RUN, "awaiting_event"];

export interface ToolState {
  runId: number;
  state: "running" | ToolOutcome;
  activity: ToolActivity;
}

export interface ComposerState {
  text: string;
  /** The draft's finished files, synced like the text; uploads still running stay in their tab. */
  files: OutgoingFile[];
  rev: number;
  /** The text was edited here and not yet acknowledged: never overwrite it with a snapshot. */
  dirty: boolean;
  /** The same, for the files. */
  filesDirty: boolean;
  focused: boolean;
  /**
   * This tab sent the draft, and the server's patch that clears it (the one no tab wrote) has not
   * come yet: until it does, an echo of this tab's own earlier write is older than the send.
   */
  clearing?: boolean;
  /** The last patch another view wrote, kept even when a dirty draft here set it aside. */
  remote?: { tabId: string | null; rev: number; text: string; files: OutgoingFile[] };
}

export interface ThreadState {
  id: number;
  rev: number;
  profile: string;
  contextRef: ContextRef | null;
  modelRef: string | null;
  /** By seq. Older pages from LoadEarlier are merged in; stored messages never change. */
  messages: WireMessage[];
  hasEarlier: boolean;
  pendingSeqs: number[];
  run: RunHead | null;
  choices: Record<string, WireChoice>;
  /**
   * The refs of the approvals of calls outside any thread (MCP proposals) about a bound thread's
   * context, oldest first, shown above its messages: the waiting ones, and those decided this session
   * (their receipts). Their rows are in `choices`.
   */
  contextChoices: string[];
  live: LiveMessage | null;
  tools: Record<string, ToolState>;
  composer: ComposerState;
  /** wanted: a doubt was seen and a subscribe should go out; inFlight: it has. */
  resync: "none" | "wanted" | "inFlight";
}

/** A file on a message being sent: shown as an attachment until the stored message lands. */
export interface OutgoingFile {
  id: number;
  name: string;
  contentType: string;
}

export interface OutgoingMessage {
  cid: string;
  threadId: number;
  parts: OutgoingPart[];
  text: string;
  files: OutgoingFile[];
  state: "sending" | "sent" | "failed";
  seq: number | null;
  error: { code: ErrorCode; message: string } | null;
  /** The model picked for the thread when this was sent; it rides the send as model_ref. */
  modelRef: string | null;
  /** What the page showed as this was sent; every attempt sends the same. */
  pageState: PageState | null;
}

export interface TargetState {
  threadId: number | null;
  /** Views currently showing it; at 0 the middleware unsubscribes after a linger. */
  refs: number;
  /** The server ended the subscription: the user lost access to it. */
  lost: boolean;
  lastViewedAt: number;
}

export interface AgentState {
  connection: {
    status: ConnectionStatus;
    tabId: string;
    deviceId: string;
    colour: number | null;
    /** A hello.ok was seen before, so the next one is a reconnect. */
    everOpened: boolean;
    /** A connect failed (backoff) since the last hello.ok: the next attempt is a retry. */
    failedOnce: boolean;
    idleAfter: number;
    pingInterval: number;
  };
  targets: Record<TargetKey, TargetState>;
  threads: Record<number, ThreadState>;
  presence: PresenceTab[];
  /** The user's last focused application, from any of their tabs and devices. */
  focus: ContextRef | null;
  outbox: Record<string, OutgoingMessage>;
  /** A composer pick the server has not confirmed yet; the next send carries it too. */
  modelPicks: Record<number, string>;
  /**
   * The pages picked for a draft's PDFs ("2-5"), by thread then thread file: this tab's only (the
   * draft syncs its files' ids), and the next send carries them.
   */
  pagePicks: Record<number, Record<number, string>>;
  /**
   * A draft PDF's page count, by thread file, once this tab has counted it: TIMED_OUT when the
   * count took too long, null when it could not be counted. None yet: its pages are not known.
   */
  pdfPages: Record<number, number | typeof TIMED_OUT | null>;
  /** The draft PDFs this tab is counting the pages of, so no other view counts them again. */
  pdfCounting: Record<number, true>;
  /**
   * The thread file each Files panel file last became on a thread, by thread then
   * `workspaceFileKey`: a file taken off and added again is a new thread file, and its pick follows.
   */
  workspaceFiles: Record<number, Record<string, number>>;
  /** This tab's part in the user's sandbox host (C5); null until the server says. */
  sandboxHost: SandboxHost | null;
  /** As the host tab: every sandbox call of the user's waiting now, from any thread or surface
   * (`sandbox.waiting`); empty while it is not the host. */
  sandboxWaiting: SandboxClientCall[];
  /** The chat commands the composer's menu lists (`hello.ok`); none until the server says. */
  commands: WireCommand[];
}

/** A Files panel file's key in `workspaceFiles`. */
export const workspaceFileKey = (workspaceId: string, path: string) => `${workspaceId}:${path}`;

export function initialAgentState(tabId: string, deviceId: string): AgentState {
  return {
    connection: {
      status: "off",
      tabId,
      deviceId,
      colour: null,
      everOpened: false,
      failedOnce: false,
      idleAfter: 7200,
      pingInterval: 25,
    },
    targets: {},
    threads: {},
    presence: [],
    focus: null,
    outbox: {},
    modelPicks: {},
    pagePicks: {},
    pdfPages: {},
    pdfCounting: {},
    workspaceFiles: {},
    sandboxHost: null,
    sandboxWaiting: [],
    commands: [],
  };
}

const DURABLE = new Set<FrameType>(DURABLE_FRAMES);
const isDurable = (frame: Frame): frame is DurableFrame => DURABLE.has(frame.type);

interface Batch {
  frames: Frame[];
  /** The target each snapshot answers, by the cid of the subscribe that asked for it. */
  subscribes: Record<string, TargetKey>;
  receivedAt: number;
}

type FrameReducers = {
  [K in FrameType]?: (state: AgentState, frame: FrameOf<K>, batch: Batch) => void;
};

function insertBySeq(messages: WireMessage[], message: WireMessage): void {
  if (messages.some((existing) => existing.seq === message.seq)) return;
  const index = messages.findIndex((existing) => existing.seq > message.seq);
  if (index === -1) messages.push(message);
  else messages.splice(index, 0, message);
}

/**
 * A snapshot lists only the open choices. Settled ones seen this session stay (a receipt, a
 * Filed chip); a pending one the snapshot no longer lists is gone; the snapshot's open ones win.
 */
function mergeChoices(
  known: Record<string, WireChoice> | undefined,
  open: WireChoice[]
): Record<string, WireChoice> {
  const settled = Object.values(known ?? {}).filter((choice) => choice.status !== "pending");
  return Object.fromEntries([...settled, ...open].map((choice) => [choice.ref, choice]));
}

/** Receipts seen this session stay; a waiting one the snapshot no longer lists is gone. */
function contextChoiceRefs(
  known: string[] | undefined,
  choices: Record<string, WireChoice>,
  open: WireChoice[]
): string[] {
  const receipts = (known ?? []).filter((ref) => choices[ref]?.status === "decided");
  return [...new Set([...receipts, ...open.map((choice) => choice.ref)])];
}

function applySnapshot(state: AgentState, frame: FrameOf<"snapshot">, batch: Batch): void {
  const existing = state.threads[frame.thread_id];
  const firstSeq = frame.messages[0]?.seq ?? Infinity;
  const older = existing?.messages.filter((message) => message.seq < firstSeq) ?? [];
  const running = frame.run && ACTIVE_RUN.includes(frame.run.status) ? frame.run : null;
  // The live buffer survives only a snapshot that brings no assistant step we lacked: a step we
  // missed (the usual cause of a gap) replaces whatever the buffer had, so it starts over.
  const known = new Set(existing?.messages.map((message) => message.seq));
  const missedStep = frame.messages.some(
    (message) => message.role === "assistant" && !known.has(message.seq)
  );
  const keepLive = running && !missedStep && existing?.live?.runId === running.id;
  const contextChoices = frame.context_choices ?? [];
  // Settled cards render from `choices`; the open ones are in both lists.
  const choices = mergeChoices(existing?.choices, [
    ...(frame.choices ?? []),
    ...frame.open_choices,
    ...contextChoices,
  ]);
  state.threads[frame.thread_id] = {
    id: frame.thread_id,
    rev: frame.rev,
    profile: frame.profile,
    contextRef: frame.context_ref,
    modelRef: frame.model_ref,
    messages: [...older, ...frame.messages],
    hasEarlier: older.length ? existing.hasEarlier : frame.has_earlier,
    pendingSeqs: frame.pending_seqs,
    run: frame.run,
    choices,
    contextChoices: contextChoiceRefs(existing?.contextChoices, choices, contextChoices),
    // A late joiner sees the typing indicator until the next part: there is no partial buffer.
    live: keepLive
      ? existing.live
      : running
        ? newLiveMessage(running.id, batch.receivedAt, true)
        : null,
    tools: keepLive ? existing.tools : {},
    composer: snapshotDraft(existing?.composer, frame.composer),
    resync: "none",
  };
  const key = frame.cid ? batch.subscribes[frame.cid] : undefined;
  if (key && state.targets[key]) {
    state.targets[key].threadId = frame.thread_id;
    state.targets[key].lost = false;
  }
}

const DURABLE_REDUCERS: FrameReducers = {
  "message.appended": (state, frame) => {
    const thread = state.threads[frame.thread_id];
    insertBySeq(thread.messages, frame.message);
    if (frame.message.role === "assistant" && thread.live) resetLiveStep(thread.live);
  },
  "run.status": (state, frame, batch) => {
    const thread = state.threads[frame.thread_id];
    thread.run = frame.run;
    if (frame.input_seqs) {
      const taken = new Set(frame.input_seqs);
      thread.pendingSeqs = thread.pendingSeqs.filter((seq) => !taken.has(seq));
    }
    if (!ACTIVE_RUN.includes(frame.run.status)) {
      thread.live = null;
      thread.tools = {};
    } else if (frame.run.status === "running" && thread.live?.runId !== frame.run.id) {
      thread.live = newLiveMessage(frame.run.id, batch.receivedAt, false);
    }
  },
  "choice.requested": (state, frame) => {
    state.threads[frame.thread_id].choices[frame.choice.ref] = frame.choice;
  },
  "choice.resolved": (state, frame) => {
    state.threads[frame.thread_id].choices[frame.choice.ref] = frame.choice;
  },
  "thread.updated": (state, frame) => {
    const thread = state.threads[frame.thread_id];
    thread.contextRef = frame.thread.context_ref;
    thread.modelRef = frame.thread.model_ref;
    // Only the pick's own confirmation settles it; an older one, or a context change, leaves it.
    if (state.modelPicks[frame.thread_id] === frame.thread.model_ref) {
      delete state.modelPicks[frame.thread_id];
    }
  },
};

function liveFor(state: AgentState, threadId: number, runId: number): LiveMessage | null {
  const live = state.threads[threadId]?.live;
  return live?.runId === runId ? live : null;
}

const EPHEMERAL_REDUCERS: FrameReducers = {
  "hello.ok": (state, frame) => {
    Object.assign(state.connection, {
      status: "open",
      colour: frame.colour,
      everOpened: true,
      failedOnce: false,
      idleAfter: frame.idle_after,
      pingInterval: frame.ping_interval,
    });
    state.focus = frame.focus;
    state.commands = frame.commands ?? []; // a server from before commands sends none
  },
  snapshot: applySnapshot,
  // A user frame: every bound thread on that context shows it. Waiting and decided ones show (a
  // receipt); an expired or cancelled one goes, since nothing expires in front of the user.
  "choice.updated": (state, frame) => {
    for (const thread of Object.values(state.threads)) {
      if (thread.profile !== "application" || thread.contextRef !== frame.context_ref) continue;
      const { choice } = frame;
      thread.choices[choice.ref] = choice;
      const shown = thread.contextChoices.includes(choice.ref);
      if (choice.status === "pending" || choice.status === "decided") {
        if (!shown) thread.contextChoices.push(choice.ref);
      } else if (shown) {
        thread.contextChoices = thread.contextChoices.filter((ref) => ref !== choice.ref);
      }
    }
  },
  "message.delta": (state, frame, batch) => {
    const live = liveFor(state, frame.thread_id, frame.run_id);
    if (live) applyDelta(live, frame, batch.receivedAt);
  },
  "tool.call": (state, frame) => {
    const thread = state.threads[frame.thread_id];
    if (!thread) return;
    thread.tools[frame.id] = { runId: frame.run_id, state: "running", activity: frame.activity };
    const live = liveFor(state, frame.thread_id, frame.run_id);
    if (live && !live.toolCallIds.includes(frame.id)) live.toolCallIds.push(frame.id);
  },
  "tool.result": (state, frame) => {
    const thread = state.threads[frame.thread_id];
    if (!thread) return;
    thread.tools[frame.id] = {
      runId: frame.run_id,
      state: frame.outcome,
      activity: frame.activity ?? thread.tools[frame.id]?.activity ?? null,
    };
  },
  "composer.patch": (state, frame) => {
    const composer = state.threads[frame.thread_id]?.composer;
    if (!composer) return;
    // The send's clear, even at a rev this tab's acks passed already: patches after it are newer.
    if (frame.tab_id === null) composer.clearing = false;
    // A patch at the held rev still applies (the same server draft, so it changes nothing twice):
    // this tab's own echo at the rev its ack stored brings the field this tab did not write.
    if (frame.rev < composer.rev) return;
    composer.rev = frame.rev;
    const files = draftFiles(frame.files);
    if (frame.tab_id === state.connection.tabId) {
      if (frame.text === composer.text) composer.dirty = false;
      if (sameFiles(files, composer.files)) composer.filesDirty = false;
      // Not after a send, though: a write still in flight when it went echoes the draft the send
      // cleared, at a rev above any this tab held then, so only the clear tells it apart.
      if (composer.clearing) return;
    } else {
      composer.remote = { tabId: frame.tab_id, rev: frame.rev, text: frame.text, files };
    }
    // Last edit wins, per field: an edit here the server has not acknowledged is newer, so it stays.
    if (!composer.dirty) composer.text = frame.text;
    if (!composer.filesDirty && !sameFiles(composer.files, files)) composer.files = files;
  },
  presence: (state, frame) => {
    state.presence = frame.tabs;
  },
  "focus.changed": (state, frame) => {
    state.focus = frame.context_ref;
  },
  "sandbox.host": (state, { role, kind, epoch, served }) => {
    const next = served === undefined ? { role, kind, epoch } : { role, kind, epoch, served };
    if (!newerHost(state.sandboxHost, next)) return;
    state.sandboxHost = next;
    if (role !== "host") state.sandboxWaiting = []; // another host runs them now
  },
  "sandbox.waiting": (state, { calls }) => {
    // A list sent before this tab stood by can arrive after: it is another host's to run now. A tab
    // with no frame yet keeps it (its first sandbox.host may follow).
    if (state.sandboxHost && state.sandboxHost.role !== "host") return;
    state.sandboxWaiting = calls;
  },
};

/** The wire's draft files (missing: none). */
function draftFiles(files: WireDraftFile[] | undefined): OutgoingFile[] {
  return (files ?? []).map((file) => ({
    id: file.id,
    name: file.name,
    contentType: file.content_type,
  }));
}

const fileIds = (files: OutgoingFile[]) => files.map((file) => file.id);

function sameIds(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

/**
 * A patch carries the whole draft: the held list stays while it names the same files, so another
 * tab's typing never hands the composer a new list (a re-render and a mirror pass per keystroke).
 */
const sameFiles = (held: OutgoingFile[], next: OutgoingFile[]) =>
  sameIds(fileIds(held), fileIds(next));

/**
 * The draft a snapshot leaves. It never replaces a dirty field; a newer server draft is remembered
 * like a remote patch, so the ack of an older in-flight write settles on it.
 */
function snapshotDraft(
  held: ComposerState | undefined,
  server: Frames["snapshot"]["composer"]
): ComposerState {
  const files = draftFiles(server.files);
  if (!held?.dirty && !held?.filesDirty) {
    return {
      text: server.text,
      files: held && sameFiles(held.files, files) ? held.files : files,
      rev: server.rev,
      dirty: false,
      filesDirty: false,
      focused: held?.focused ?? false,
    };
  }
  if (server.rev <= held.rev) return held;
  return {
    ...held,
    // A newer server draft is past any send's clear: this tab's own echoes apply again.
    clearing: false,
    text: held.dirty ? held.text : server.text,
    files: held.filesDirty || sameFiles(held.files, files) ? held.files : files,
    rev: server.rev,
    remote: { tabId: null, rev: server.rev, text: server.text, files },
  };
}

function applyFrame(state: AgentState, frame: Frame, batch: Batch): void {
  if (isDurable(frame)) {
    const thread = state.threads[frame.thread_id];
    if (!thread) return;
    const verdict = classifyRev(thread.rev, frame.rev);
    if (verdict === "gap") {
      if (thread.resync === "none") thread.resync = "wanted";
      return;
    }
    if (verdict === "drop") return;
    thread.rev = frame.rev;
    const reduce = DURABLE_REDUCERS[frame.type] as FrameReducers[FrameType];
    reduce?.(state, frame as never, batch);
    return;
  }
  const reduce = EPHEMERAL_REDUCERS[frame.type] as FrameReducers[FrameType];
  reduce?.(state, frame as never, batch);
}

function forgetOutgoing(state: AgentState, threadId: number): void {
  const seqs = new Set(state.threads[threadId]?.messages.map((message) => message.seq));
  for (const message of Object.values(state.outbox)) {
    if (message.threadId === threadId && message.seq !== null && seqs.has(message.seq)) {
      delete state.outbox[message.cid];
    }
  }
}

const agentSlice = createSlice({
  name: "agent",
  initialState: initialAgentState("", ""),
  reducers: {
    identityAssigned(state, action: PayloadAction<{ tabId: string; deviceId: string }>) {
      state.connection.tabId = action.payload.tabId;
      state.connection.deviceId = action.payload.deviceId;
    },
    connectionChanged(state, action: PayloadAction<ConnectionStatus>) {
      state.connection.status = action.payload;
      if (action.payload === "backoff") state.connection.failedOnce = true;
    },
    framesReceived(state, action: PayloadAction<Batch>) {
      const touched = new Set<number>();
      for (const frame of action.payload.frames) {
        applyFrame(state, frame, action.payload);
        if ("thread_id" in frame) touched.add(frame.thread_id);
      }
      touched.forEach((threadId) => forgetOutgoing(state, threadId));
    },
    targetRetained(state, action: PayloadAction<{ key: TargetKey; now: number }>) {
      const { key, now } = action.payload;
      const target = (state.targets[key] ??= {
        threadId: null,
        refs: 0,
        lost: false,
        lastViewedAt: now,
      });
      target.refs += 1;
      target.lastViewedAt = now;
    },
    targetReleased(state, action: PayloadAction<{ key: TargetKey; now: number }>) {
      const target = state.targets[action.payload.key];
      if (!target) return;
      target.refs = Math.max(0, target.refs - 1);
      target.lastViewedAt = action.payload.now;
    },
    /** The middleware unsubscribed it; its thread state goes too unless another target shows it. */
    targetDropped(state, action: PayloadAction<TargetKey>) {
      const threadId = state.targets[action.payload]?.threadId;
      delete state.targets[action.payload];
      const shown = Object.values(state.targets).some((target) => target.threadId === threadId);
      if (threadId != null && !shown) delete state.threads[threadId];
    },
    targetLost(state, action: PayloadAction<{ threadId: number }>) {
      for (const target of Object.values(state.targets)) {
        if (target.threadId === action.payload.threadId) target.lost = true;
      }
      delete state.threads[action.payload.threadId];
    },
    resyncSent(state, action: PayloadAction<number[]>) {
      for (const threadId of action.payload) {
        const thread = state.threads[threadId];
        if (thread) thread.resync = "inFlight";
      }
    },
    /** A doubt the middleware saw itself (reconnect, visible again, a refused decide, a quiet run). */
    resyncWanted(state, action: PayloadAction<number[]>) {
      for (const threadId of action.payload) {
        const thread = state.threads[threadId];
        if (thread && thread.resync === "none") thread.resync = "wanted";
      }
    },
    earlierLoaded(
      state,
      action: PayloadAction<{ threadId: number; messages: WireMessage[]; hasEarlier: boolean }>
    ) {
      const thread = state.threads[action.payload.threadId];
      if (!thread) return;
      action.payload.messages.forEach((message) => insertBySeq(thread.messages, message));
      thread.hasEarlier = action.payload.hasEarlier;
    },
    composerEdited(state, action: PayloadAction<{ threadId: number; text: string }>) {
      const composer = state.threads[action.payload.threadId]?.composer;
      if (!composer || composer.text === action.payload.text) return;
      composer.text = action.payload.text;
      composer.dirty = true;
    },
    /** The server took a draft write: its text, its file ids, or both. */
    composerAcked(
      state,
      action: PayloadAction<{ threadId: number; rev: number; text?: string; files?: number[] }>
    ) {
      const composer = state.threads[action.payload.threadId]?.composer;
      if (!composer) return;
      const { text, files, rev } = action.payload;
      if (text !== undefined && composer.text === text) composer.dirty = false;
      if (files && sameIds(fileIds(composer.files), files)) composer.filesDirty = false;
      // Another view's patch, set aside while this write was in flight, was written after it.
      const remote = composer.remote;
      if (remote?.rev === composer.rev && composer.rev > rev) {
        if (!composer.dirty) composer.text = remote.text;
        if (!composer.filesDirty && !sameFiles(composer.files, remote.files)) {
          composer.files = remote.files;
        }
      }
      composer.rev = Math.max(composer.rev, rev);
    },
    /** The server refused the files written here: drop them for its own, which a resync brings. */
    composerFilesRefused(state, action: PayloadAction<{ threadId: number; files: number[] }>) {
      const composer = state.threads[action.payload.threadId]?.composer;
      // A list edited since the refused one was sent is newer: it stays dirty and goes next.
      if (composer && sameIds(fileIds(composer.files), action.payload.files)) {
        composer.filesDirty = false;
      }
    },
    composerFilesEdited(state, action: PayloadAction<{ threadId: number; files: OutgoingFile[] }>) {
      const composer = state.threads[action.payload.threadId]?.composer;
      if (!composer || sameFiles(composer.files, action.payload.files)) return;
      composer.files = action.payload.files;
      composer.filesDirty = true;
    },
    composerFocused(state, action: PayloadAction<{ threadId: number; focused: boolean }>) {
      const composer = state.threads[action.payload.threadId]?.composer;
      if (composer) composer.focused = action.payload.focused;
    },
    messageQueued(
      state,
      action: PayloadAction<{
        cid: string;
        threadId: number;
        parts: OutgoingPart[];
        text: string;
        files: OutgoingFile[];
        modelRef: string | null;
        pageState?: PageState | null;
      }>
    ) {
      state.outbox[action.payload.cid] = {
        ...action.payload,
        pageState: action.payload.pageState ?? null,
        state: "sending",
        seq: null,
        error: null,
      };
      const composer = state.threads[action.payload.threadId]?.composer;
      // The send clears the draft on the server too; the composer.patch that says so follows.
      if (composer) {
        Object.assign(composer, {
          text: "",
          dirty: false,
          files: [],
          filesDirty: false,
          clearing: true,
        });
      }
      delete state.pagePicks[action.payload.threadId];
    },
    messageAcked(state, action: PayloadAction<{ cid: string; seq: number }>) {
      const message = state.outbox[action.payload.cid];
      if (!message) return;
      Object.assign(message, { state: "sent", seq: action.payload.seq });
      forgetOutgoing(state, message.threadId);
    },
    messageFailed(state, action: PayloadAction<{ cid: string; code: ErrorCode; message: string }>) {
      const outgoing = state.outbox[action.payload.cid];
      if (!outgoing) return;
      outgoing.state = "failed";
      outgoing.error = { code: action.payload.code, message: action.payload.message };
    },
    messageRetried(state, action: PayloadAction<string>) {
      const outgoing = state.outbox[action.payload];
      if (outgoing) Object.assign(outgoing, { state: "sending", error: null });
    },
    /** The user took a failed message back into the composer, or dismissed it. */
    messageDiscarded(state, action: PayloadAction<string>) {
      delete state.outbox[action.payload];
    },
    modelPicked(state, action: PayloadAction<{ threadId: number; modelRef: string }>) {
      state.modelPicks[action.payload.threadId] = action.payload.modelRef;
    },
    /** The pick could not be saved: show the thread's model again, unless a newer pick replaced it. */
    modelPickFailed(state, action: PayloadAction<{ threadId: number; modelRef: string }>) {
      if (state.modelPicks[action.payload.threadId] === action.payload.modelRef) {
        delete state.modelPicks[action.payload.threadId];
      }
    },
    /** A draft PDF's pages, or null for the whole file. */
    pagesPicked(
      state,
      action: PayloadAction<{ threadId: number; fileId: number; pages: string | null }>
    ) {
      const { threadId, fileId, pages } = action.payload;
      if (pages) {
        (state.pagePicks[threadId] ??= {})[fileId] = pages;
        return;
      }
      const picks = state.pagePicks[threadId];
      if (!picks) return;
      delete picks[fileId];
      if (Object.keys(picks).length === 0) delete state.pagePicks[threadId];
    },
    /** This tab started counting a draft PDF's pages. */
    pdfPagesCounting(state, action: PayloadAction<{ fileId: number }>) {
      state.pdfCounting[action.payload.fileId] = true;
    },
    /**
     * A draft PDF's page count, counted in this tab: TIMED_OUT when that took too long, null when
     * it could not be. A pick that reaches past the count (carried from an earlier version of the
     * file) is dropped, so the default for the count applies instead.
     */
    pdfPagesCounted(
      state,
      action: PayloadAction<{ fileId: number; pages: number | typeof TIMED_OUT | null }>
    ) {
      const { fileId, pages } = action.payload;
      delete state.pdfCounting[fileId];
      state.pdfPages[fileId] = pages;
      if (pages == null || pages === TIMED_OUT) return;
      for (const [threadId, picks] of Object.entries(state.pagePicks)) {
        const pick = picks[fileId];
        if (!pick || Math.max(...(pick.match(/\d+/g) ?? []).map(Number)) <= pages) continue;
        delete picks[fileId];
        if (Object.keys(picks).length === 0) delete state.pagePicks[Number(threadId)];
      }
    },
    /**
     * A Files panel file went on the draft as `fileId`. Added before as another thread file (taken
     * off, its link gone), it keeps the pages picked for that one. Not its page count: the path
     * may hold another file by now (a new thread file is a new one), so it is counted again.
     */
    workspaceFileAdded(
      state,
      action: PayloadAction<{ threadId: number; key: string; fileId: number }>
    ) {
      const { threadId, key, fileId } = action.payload;
      const added = (state.workspaceFiles[threadId] ??= {});
      const earlier = added[key];
      added[key] = fileId;
      if (earlier == null || earlier === fileId) return;
      const picks = state.pagePicks[threadId];
      if (picks?.[earlier] && !picks[fileId]) picks[fileId] = picks[earlier];
    },
    agentReset(state) {
      return initialAgentState(state.connection.tabId, state.connection.deviceId);
    },
  },
});

export const agentActions = agentSlice.actions;
export default agentSlice.reducer;
